<template>
	<page-container class="container no-heading-image" :page="page">
		<div v-if="page.headingImages">
			<heading-image
				v-for="(item, index) in page.headingImages"
				:key="index"
				:heading-image="item"
				:title="page.title || page.header"
			/>
		</div>

		<content-blocks v-if="page.contentblocks.length" :items="page.contentblocks" />

		<main-content
			:title="(page.title || page.header) ?? 'Page not found'"
			:subtitle="page.subtitle"
			:content="page.content"
			:side-image="{
				image: page.sideImage,
				imageAlt: page.sideImageAlt,
				imageWebp: page.sideImageeWebp,
			}"
		/>

		<section class="main-content cookie-content">
			<div v-if="cookies" :key="locale" class="row">
				<div class="columns column12">
					<div href="#" class="button" @click="openConsent">{{ $t('consentOpen') }}</div>
					<h2>Cookies</h2>
					<div
						v-for="category in ['Necessary', 'Performance', 'Functional', 'Advertising', 'Unclassified']"
						:key="category"
					>
						<div v-if="cookies.has(category.toLowerCase())">
							<h3>{{ $t(`consentCategory${category}`) }}</h3>
							<div v-if="category !== 'unclassified'">
								<div v-html="defaults[locale].cookieConsent[`content${category}`]" />
							</div>
							<div class="responsive-table">
								<table class="cookie-table">
									<colgroup>
										<col style="width: 20%" />
										<col style="width: 15%" />
										<col style="width: 55%" />
										<col style="width: 10%" />
									</colgroup>
									<thead>
										<tr>
											<th>{{ $t('consentCookieName') }}</th>
											<th>{{ $t('consentCookieProvider') }}</th>
											<th>{{ $t('consentCookiePurpose') }}</th>
											<th>{{ $t('consentCookieExpiry') }}</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="cookie in cookies.get(category.toLowerCase())" :key="cookie.name">
											<td>{{ cookie.name }}</td>
											<td>
												<a v-if="cookie.q2" target="_blank" :href="cookie.q2">
													{{ cookie.q1 }}</a
												>
												<span v-else>{{ cookie.q1 }}</span>
											</td>
											<td>{{ cookie.desc }}</td>
											<td>{{ cookie.expiration_time }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<div v-if="page.showGroupsForm" class="form-wrapper">
			<div class="row">
				<div class="columns column12">
					<contact-form name="groups" :form="form" />
				</div>
			</div>
		</div>

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:subtitle="page.frequentlyAskedQuestion.subtitle"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { locale } = useI18n();
const { openConsent, cfInit, cfDestroy } = useCookieConsent();

await fetchPage();

const { data: form } = await useWebsiteFetch('groups', {
	query: { language: locale.value },
	key: `${locale.value}/groups`,
});

useHead({
	bodyAttrs: {
		class: 'subpage',
	},
});

onMounted(() => {
	cfInit(initCookieFirst);
});

onBeforeUnmount(() => {
	cfDestroy();
});

const initCookieFirst = () => {
	window.CookieFirst.fetchLatestScan()
		.then((result) => {
			cookies.value = getCookiesByCategory(result.cookies);
		})
		.catch((error) => {
			// eslint-disable-next-line no-console
			console.error(error);
		});
};

const getCookiesByCategory = (cookies) => {
	const cookiesByCategory = new Map();

	cookies.forEach(function (cookie) {
		if (!cookiesByCategory.has(cookie.cat)) {
			cookiesByCategory.set(cookie.cat, []);
		}
		cookiesByCategory.get(cookie.cat).push(cookie);
	});

	return cookiesByCategory;
};
</script>

<style lang="scss" scoped>
.main-content.cookie-content {
	padding-top: 0;

	h2 {
		font-size: 2.2em;
	}

	.button {
		margin: 0 0 50px;
	}
}

h3 {
	margin: 25px 0;
	font-size: 1.7em;
}

table {
	width: 100%;
	min-width: 800px;
	text-align: left;

	th,
	td {
		padding: 10px;
		border-bottom: 1px solid #ccc;
	}
}

.responsive-table {
	width: 100%;
	overflow-x: auto;
	scrollbar-width: thin;
}
</style>
